import { useEffect, useRef, useState } from "react";
import { Modal, Input, Spinner, toast } from "@heroui/react";
import { Icon } from "@iconify/react";
import { Button } from "src/components/button";
import { modalApi } from "src/infra/modals";
import { FirebaseApi } from "src/lib/firebase";
import type { OpenInvoiceRow } from "src/lib/firebase/api";

type Props = {
	row: OpenInvoiceRow;
};

function fmtMoney(n: number): string {
	return (
		"₪" +
		n.toLocaleString("he-IL", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})
	);
}

function fmtDate(ms: number): string {
	return new Date(ms).toLocaleDateString("he-IL", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
}

const ERROR_MESSAGES: Record<string, string> = {
	invoice_missing: "חשבונית לא נמצאה",
	already_paid: "החשבונית כבר שולמה",
	invalid_invoice_total: "סכום החשבונית אינו תקין",
	order_not_found: "ההזמנה לא נמצאה",
	missing_store_config: "הגדרות הסליקה של החנות חסרות",
	internal: "שגיאה בלתי צפויה. נסה שוב.",
};

type LinkState =
	| { status: "creating" }
	| { status: "ready"; url: string; expiresAt: number; reused: boolean }
	| { status: "error"; message: string };

/**
 * Admin modal — creates (or reuses) a HYP payment link for an open invoice
 * and offers copy / WhatsApp share. The link is created server-side with the
 * invoice's full amount; the customer pays at /pay-invoice/{token}.
 */
export function CreateInvoicePaymentLinkModal({ row }: Props) {
	const [state, setState] = useState<LinkState>({ status: "creating" });
	const calledRef = useRef(false);

	useEffect(() => {
		if (calledRef.current) return;
		calledRef.current = true;

		FirebaseApi.api
			.createHypInvoicePaymentLink({
				orderId: row.orderId,
				origin: window.location.origin,
			})
			.then((res) => {
				if (res.success && res.data?.success && res.data.url) {
					setState({
						status: "ready",
						url: res.data.url,
						expiresAt: res.data.expiresAt ?? 0,
						reused: res.data.reused === true,
					});
				} else {
					const code = res.data?.error ?? "internal";
					setState({
						status: "error",
						message: ERROR_MESSAGES[code] ?? ERROR_MESSAGES.internal,
					});
				}
			});
	}, [row.orderId]);

	const handleCopy = async (url: string) => {
		try {
			await navigator.clipboard.writeText(url);
			toast.success("הלינק הועתק");
		} catch {
			toast.danger("העתקה נכשלה — ניתן לסמן ולהעתיק ידנית");
		}
	};

	const whatsappHref = (url: string) => {
		const text = `שלום ${row.displayName},\nלתשלום חשבונית מס׳ ${row.invoiceNumber} על סך ${fmtMoney(row.total)}:\n${url}`;
		return `https://wa.me/?text=${encodeURIComponent(text)}`;
	};

	return (
		<Modal.Backdrop
			isOpen
			onOpenChange={(open) => {
				if (!open) modalApi.closeModal("createInvoicePaymentLink");
			}}
		>
			<Modal.Container size="md" scroll="inside">
				<Modal.Dialog>
					<Modal.Header>
						<Modal.Heading>
							<div className="text-start" dir="rtl">
								לינק לתשלום — חשבונית {row.invoiceNumber}
							</div>
						</Modal.Heading>
					</Modal.Header>

					<Modal.Body>
						<div className="space-y-5" dir="rtl">
							{/* Invoice summary */}
							<div className="rounded-lg border border-[var(--border)] bg-[var(--background)] p-4 space-y-1 text-sm">
								<div>
									<span className="text-[var(--muted)]">לקוח: </span>
									<span className="font-medium">{row.displayName}</span>
								</div>
								<div>
									<span className="text-[var(--muted)]">סכום לתשלום: </span>
									<span className="font-semibold">{fmtMoney(row.total)}</span>
								</div>
							</div>

							{state.status === "creating" && (
								<div className="flex items-center justify-center gap-3 py-6">
									<Spinner size="sm" />
									<span className="text-sm text-[var(--muted)]">
										יוצר לינק לתשלום…
									</span>
								</div>
							)}

							{state.status === "error" && (
								<div className="rounded-lg border border-[var(--danger)] bg-[var(--background)] p-4 text-sm text-[var(--danger)]">
									{state.message}
								</div>
							)}

							{state.status === "ready" && (
								<>
									{state.reused && (
										<p className="text-xs text-[var(--muted)]">
											קיים כבר לינק פעיל לחשבונית זו — הוחזר אותו לינק.
										</p>
									)}

									<div className="flex flex-col gap-1">
										<label className="text-sm font-medium">
											הלינק לתשלום
										</label>
										<div className="flex gap-2">
											<Input
												value={state.url}
												readOnly
												aria-label="לינק לתשלום"
												className="flex-1 direction-ltr text-left"
												onFocus={(e) => e.currentTarget.select()}
											/>
											<button
												type="button"
												className="inline-flex items-center gap-1 px-3 rounded border border-[var(--border)] text-sm font-semibold hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
												onClick={() => handleCopy(state.url)}
											>
												<Icon icon="lucide:copy" width={14} height={14} />
												העתק
											</button>
										</div>
										{state.expiresAt > 0 && (
											<p className="text-xs text-[var(--muted)]">
												בתוקף עד {fmtDate(state.expiresAt)} · לשימוש חד־פעמי
											</p>
										)}
									</div>

									<a
										href={whatsappHref(state.url)}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center justify-center gap-2 w-full px-4 py-2 rounded-lg text-sm font-semibold border border-[var(--border)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
									>
										<Icon icon="lucide:message-circle" width={16} height={16} />
										שיתוף בוואטסאפ
									</a>
								</>
							)}
						</div>
					</Modal.Body>

					<Modal.Footer>
						<div className="flex justify-start" dir="rtl">
							<Button
								onPress={() =>
									modalApi.closeModal("createInvoicePaymentLink")
								}
							>
								סגור
							</Button>
						</div>
					</Modal.Footer>
				</Modal.Dialog>
			</Modal.Container>
		</Modal.Backdrop>
	);
}
